/**
 * Every restaurant, and opening a new one.
 *
 * Opening asks exactly what `scripts/open_restaurant.py` asks, and the server
 * checks it the same way (`identity.opening.NewRestaurant`): the time zone has
 * no default because a restaurant can be anywhere. The owner is emailed a
 * link to set their own password; nobody here ever types or sees it.
 *
 * A new restaurant can start on a free trial of a plan above Starter: it
 * opens on Starter as always, and the trial starts on top of it.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  displayLocale,
  useOpenRestaurant,
  usePlatformPlanCatalog,
  usePlatformRestaurants,
  COUNTRIES,
  LOCALES,
  named,
  supportedCurrencies,
  usePlatformRegionSuggestion,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Card,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Sheet,
} from '@restaurant/ui';

import { addDays, dayOf, formatDay, formatMoment, isAddress, suggestAddress } from '../words';

export function RestaurantsPage() {
  const [search, setSearch] = useState('');
  const [opening, setOpening] = useState(false);
  const list = usePlatformRestaurants(search);
  const locale = displayLocale();

  return (
    <>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">Restaurants</h1>
          <p className="pf__sub">Every restaurant on the platform, newest first.</p>
        </div>
        <Button variant="primary" onClick={() => setOpening(true)}>
          Open a restaurant
        </Button>
      </header>

      <div className="pf__toolbar">
        <Field label="Find" hint="By name or address">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="casa-lola"
          />
        </Field>
      </div>

      <Card>
        {list.isPending && <Loading label="Loading restaurants" />}
        {list.isError && <ErrorNotice message={list.error.message} />}
        {list.data && list.data.length === 0 && (
          <Empty
            title={search ? 'No restaurant matches that' : 'No restaurants yet'}
            description={
              search ? 'Try part of the name or the address.' : 'Open the first one.'
            }
          />
        )}
        {list.data && list.data.length > 0 && (
          <div className="pf__table-wrap">
            <table className="pf__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th className="pf__num">Sites</th>
                  <th>Opened</th>
                  <th>Last order</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link className="pf__rowlink" to={`/restaurants/${r.id}`}>
                        {r.name}
                      </Link>
                    </td>
                    <td className="pf__mono">{r.slug}</td>
                    <td>{r.plan_name ?? '—'}</td>
                    <td>
                      {r.status === 'suspended' ? (
                        <Badge tone="danger">Suspended</Badge>
                      ) : r.status === 'trial' ? (
                        <>
                          <Badge tone="accent">Trial</Badge>
                          {r.trial_ends_at && (
                            <span className="pf__faint">
                              {' '}
                              until {formatDay(dayOf(r.trial_ends_at), locale)}
                            </span>
                          )}
                        </>
                      ) : (
                        <Badge tone="ok">Active</Badge>
                      )}
                    </td>
                    <td className="pf__num">{r.sites}</td>
                    <td>{formatMoment(r.created_at, locale)}</td>
                    <td>{r.last_order_on ? formatDay(r.last_order_on, locale) : 'None yet'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {opening && <OpenRestaurantSheet onDismiss={() => setOpening(false)} />}
    </>
  );
}

/** Every IANA zone the browser knows, for the time zone field's suggestions. */
function zones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
}

function OpenRestaurantSheet({ onDismiss }: { onDismiss: () => void }) {
  const open = useOpenRestaurant();
  const catalog = usePlatformPlanCatalog();
  const navigate = useNavigate();
  const zoneList = useMemo(zones, []);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [addressTouched, setAddressTouched] = useState(false);
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [timezone, setTimezone] = useState('');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('');
  const [locale, setLocale] = useState('');
  const [siteName, setSiteName] = useState('');
  const [siteCode, setSiteCode] = useState('MAIN');
  const [onTrial, setOnTrial] = useState(false);
  const [trialPlan, setTrialPlan] = useState('');
  const [trialDays, setTrialDays] = useState('30');

  const opening = catalog.data?.plans.find((p) => p.key === catalog.data?.opening_plan);
  const trialPlans = catalog.data?.plans.filter((p) => p.tier > (opening?.tier ?? 0)) ?? [];
  const trialKey = trialPlan || trialPlans[0]?.key || '';
  const maxDays = catalog.data?.trial_max_days ?? 0;
  const dayCount = /^\d{1,3}$/.test(trialDays.trim()) ? Number(trialDays) : NaN;
  const trialOk = !onTrial || (trialKey !== '' && dayCount >= 1 && dayCount <= maxDays);

  const slug = addressTouched ? address : suggestAddress(name);
  const suggestion = usePlatformRegionSuggestion(country || null);
  // A country picked fills in its currency and format once; either can then
  // be changed, for a restaurant that takes another currency.
  useEffect(() => {
    if (!suggestion.data || suggestion.data.country !== country) return;
    if (suggestion.data.currency) setCurrency(suggestion.data.currency);
    // English as the country writes it: the product's words are English, and
    // dates in another language would sit in the middle of them.
    setLocale(`en-${suggestion.data.country}`);
  }, [suggestion.data, country]);
  const countries = useMemo(() => named(COUNTRIES, 'region'), []);
  const currencies = useMemo(() => named(supportedCurrencies(), 'currency'), []);
  const formats = useMemo(
    () =>
      named(locale && !LOCALES.includes(locale) ? [...LOCALES, locale] : LOCALES, 'language'),
    [locale],
  );
  const zoneKnown = zoneList.length === 0 || zoneList.includes(timezone);
  const ready =
    name.trim() !== '' &&
    isAddress(slug) &&
    ownerName.trim() !== '' &&
    email.includes('@') &&
    timezone !== '' &&
    zoneKnown &&
    country !== '' &&
    currency !== '' &&
    siteCode.trim() !== '' &&
    trialOk;

  return (
    <Sheet
      title="Open a restaurant"
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!ready}
            loading={open.isPending}
            onClick={() =>
              open.mutate(
                {
                  restaurant_name: name.trim(),
                  slug,
                  owner_name: ownerName.trim(),
                  email: email.trim(),
                  timezone,
                  country,
                  currency,
                  locale: locale || null,
                  location_name: siteName.trim() || null,
                  location_code: siteCode.trim(),
                  trial_plan: onTrial ? trialKey : null,
                  trial_days: onTrial ? dayCount : null,
                },
                { onSuccess: (opened) => navigate(`/restaurants/${opened.id}`) },
              )
            }
          >
            Open it and email the owner
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        The restaurant starts on Starter with one site. Its owner is emailed a link to set their
        own password, and signs in with the address below.
      </p>
      <Field label="Restaurant name">
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field
        label="Address"
        hint="What the owner types to sign in: lower case, digits and hyphens. It cannot be changed later."
        error={
          slug !== '' && !isAddress(slug)
            ? 'Use 3 to 63 lower-case letters, digits and hyphens, not starting or ending with a hyphen.'
            : undefined
        }
      >
        <Input
          className="pf__mono"
          value={slug}
          onChange={(e) => {
            setAddressTouched(true);
            setAddress(e.target.value.toLowerCase());
          }}
          autoCapitalize="none"
          spellCheck={false}
        />
      </Field>
      <Field label="Owner's name">
        <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
      </Field>
      <Field label="Owner's email" hint="Where the set-password link goes.">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      {/* Asked, never assumed: every restaurant used to open in Britain, in
          pounds, and its owner's first sight was the wrong money. */}
      <Field label="Country" hint="Where it trades. Fills in its currency and number format.">
        <select
          className="ui-input"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        >
          <option value="">Choose a country</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Currency" hint="What it charges in. Can be changed later in Settings.">
        <select
          className="ui-input"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          <option value="">Choose a currency</option>
          {currencies.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name} ({c.code})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Numbers and dates" hint="How prices and dates are written.">
        <select className="ui-input" value={locale} onChange={(e) => setLocale(e.target.value)}>
          <option value="">English, as the country writes it</option>
          {formats.map((f) => (
            <option key={f.code} value={f.code}>
              {f.name} ({f.code})
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Time zone of the first site"
        hint="Where the restaurant is, e.g. America/Bogota. Every business day is counted on it."
        error={timezone !== '' && !zoneKnown ? 'Choose a time zone from the list.' : undefined}
      >
        <Input
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          list="pf-zones"
          autoCapitalize="none"
          spellCheck={false}
        />
      </Field>
      <datalist id="pf-zones">
        {zoneList.map((zone) => (
          <option key={zone} value={zone} />
        ))}
      </datalist>
      <Field label="First site's name" hint="Leave empty to use the restaurant's name.">
        <Input value={siteName} onChange={(e) => setSiteName(e.target.value)} />
      </Field>
      <Field label="First site's code" hint="A short code for the site.">
        <Input value={siteCode} onChange={(e) => setSiteCode(e.target.value.toUpperCase())} />
      </Field>
      {trialPlans.length > 0 && (
        <label className="pf__check">
          <input
            type="checkbox"
            checked={onTrial}
            onChange={(e) => setOnTrial(e.target.checked)}
          />
          Start on a free trial of a higher plan
        </label>
      )}
      {onTrial && (
        <>
          <Field label="Plan to try">
            <select
              className="ui-input"
              value={trialKey}
              onChange={(e) => setTrialPlan(e.target.value)}
            >
              {trialPlans.map((plan) => (
                <option key={plan.key} value={plan.key}>
                  {plan.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Days"
            hint={
              Number.isNaN(dayCount) || dayCount < 1 || dayCount > maxDays
                ? `From 1 to ${maxDays}.`
                : `Until ${formatDay(addDays(new Date().toISOString().slice(0, 10), dayCount), displayLocale())}, then ${opening?.name ?? 'the opening plan'} by itself. The owner is told.`
            }
            error={
              trialDays.trim() !== '' &&
              (Number.isNaN(dayCount) || dayCount < 1 || dayCount > maxDays)
                ? `From 1 to ${maxDays}.`
                : undefined
            }
          >
            <Input
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              inputMode="numeric"
            />
          </Field>
        </>
      )}
      <ActionError error={open.error} action="Opening the restaurant" />
    </Sheet>
  );
}
